import { computed, nextTick, shallowRef, watch, type Ref } from 'vue';
import { CHECKOUT_BLUR_DELAY, CHECKOUT_KEYBOARD_WAIT } from '../common/constants/checkoutViewport';
import type { VerticalBounds } from '../interfaces/checkoutViewport.interface';
import {
  getCheckoutFieldScroll,
  getCheckoutFocusSpace,
  getCheckoutSectionScroll,
  getCheckoutViewport,
} from '../utils/checkoutViewport';

export function useCheckoutViewport(
  scrollContainer: Ref<HTMLElement | null>,
  isVisible: Ref<boolean>,
  enabled: boolean,
  needsFocusSpace: Ref<boolean>,
  delayScrollForKeyboard = false,
) {
  const visibleArea = shallowRef<VerticalBounds | null>(null);
  const focusedField = shallowRef<HTMLElement | null>(null);
  const focusSpace = shallowRef(0);
  const hasFocusSpace = computed(() => enabled && needsFocusSpace.value && !!focusedField.value);
  let scrollTarget: HTMLElement | null = null;
  let canReassertReveal = false;
  let heightBeforeKeyboard: number | null = null;
  let animationFrameId: number | null = null;
  let blurTimer: ReturnType<typeof setTimeout> | undefined;
  let keyboardTimer: ReturnType<typeof setTimeout> | undefined;

  const viewportStyle = computed(() => visibleArea.value
    ? {
      '--checkout-visible-bottom': `${visibleArea.value.bottom}px`,
      '--checkout-visible-height': `${visibleArea.value.bottom - visibleArea.value.top}px`,
      '--checkout-focus-space': `${focusSpace.value}px`,
    }
    : undefined);

  function updateFocusSpace(): void {
    const scroller = scrollContainer.value;
    const submitButton = scroller?.querySelector<HTMLElement>('[data-checkout-submit]');
    const content = scroller?.querySelector<HTMLElement>('[data-checkout-content]');
    if (!scroller || !submitButton || !content) return;

    if (!hasFocusSpace.value) {
      focusSpace.value = 0;
      return;
    }

    const submitOffsetTop = submitButton.getBoundingClientRect().top
      - scroller.getBoundingClientRect().top
      + scroller.scrollTop;

    focusSpace.value = getCheckoutFocusSpace(
      scroller.clientHeight,
      content.getBoundingClientRect().height,
      submitOffsetTop,
      focusSpace.value,
    );
  }

  function cancelReveal(): void {
    scrollTarget = null;
    canReassertReveal = false;
    heightBeforeKeyboard = null;
    clearTimeout(keyboardTimer);
  }

  function revealField(): void {
    const scroller = scrollContainer.value;
    const field = scrollTarget;
    if (!isVisible.value || !scroller || !field || !visibleArea.value) return;
    if (!scroller.contains(field) || !field.getClientRects().length) return;

    const scrollerBounds = scroller.getBoundingClientRect();
    const visibleHeight = visibleArea.value.bottom - visibleArea.value.top;
    const visibleTop = Math.max(0, scrollerBounds.top);
    const visibleBounds = {
      top: visibleTop,
      bottom: Math.min(scrollerBounds.bottom, visibleTop + visibleHeight),
    };
    if (hasFocusSpace.value) {
      const fields = scroller.querySelector<HTMLElement>('[data-checkout-fields]');
      const sectionTop = (fields ?? field).getBoundingClientRect().top;
      const scrollDelta = getCheckoutSectionScroll(sectionTop, visibleBounds.top);
      if (scrollDelta) scroller.scrollTo({ top: scroller.scrollTop + scrollDelta, behavior: 'smooth' });
      return;
    }

    const submitButton = scroller.querySelector<HTMLElement>('[data-checkout-submit]');
    const scrollDelta = getCheckoutFieldScroll(field.getBoundingClientRect(), visibleBounds, submitButton?.getBoundingClientRect());
    if (scrollDelta) scroller.scrollTop += scrollDelta;
  }

  function checkFieldVisible(): void {
    const scroller = scrollContainer.value;
    const field = scrollTarget;
    if (!canReassertReveal || heightBeforeKeyboard !== null || !scroller || !field || !visibleArea.value) return;

    const visibleTop = Math.max(0, scroller.getBoundingClientRect().top);
    if (field.getBoundingClientRect().top >= visibleTop) return;

    canReassertReveal = false;
    revealField();
  }

  function updateViewport(): void {
    if (!enabled || !isVisible.value || animationFrameId !== null) return;
    animationFrameId = requestAnimationFrame(() => {
      animationFrameId = null;
      visibleArea.value = getCheckoutViewport(globalThis.window.innerHeight, globalThis.window.visualViewport);
      updateFocusSpace();
      if (heightBeforeKeyboard === visibleArea.value.bottom - visibleArea.value.top) return;

      heightBeforeKeyboard = null;
      clearTimeout(keyboardTimer);
      nextTick(revealField);
    });
  }

  function focusField(field: HTMLElement | null): void {
    if (!enabled) return;
    const keyboardIsOpen = !!focusedField.value;
    clearTimeout(blurTimer);
    focusedField.value = field;
    scrollTarget = field;
    canReassertReveal = true;
    clearTimeout(keyboardTimer);
    heightBeforeKeyboard = delayScrollForKeyboard && !keyboardIsOpen && visibleArea.value
      ? visibleArea.value.bottom - visibleArea.value.top
      : null;
    if (heightBeforeKeyboard !== null) {
      keyboardTimer = setTimeout(() => {
        heightBeforeKeyboard = null;
        revealField();
      }, CHECKOUT_KEYBOARD_WAIT);
    }
    updateViewport();
  }

  function blurField(field: HTMLElement | null): void {
    if (scrollTarget === field) cancelReveal();
    if (focusedField.value !== field) return;

    clearTimeout(blurTimer);
    blurTimer = setTimeout(() => {
      if (focusedField.value === field) focusedField.value = null;
    }, CHECKOUT_BLUR_DELAY);
  }

  function onFocusIn(event: FocusEvent): void {
    const target = event.target;
    if (target instanceof HTMLElement && target.matches('input, textarea, [contenteditable="true"]')) {
      focusField(target.closest<HTMLElement>('[data-checkout-field]') ?? target);
      return;
    }
    cancelReveal();
  }

  function onFocusOut(event: FocusEvent): void {
    if (event.target instanceof HTMLElement) {
      blurField(event.target.closest<HTMLElement>('[data-checkout-field]') ?? event.target);
    }
  }

  watch(isVisible, (visible, _previous, onCleanup) => {
    if (!enabled || !visible || globalThis.window === undefined) return;
    const visualViewport = globalThis.window.visualViewport;
    visibleArea.value = getCheckoutViewport(globalThis.window.innerHeight, visualViewport);
    globalThis.window.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('scroll', updateViewport);
    updateViewport();

    onCleanup(() => {
      globalThis.window.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('scroll', updateViewport);
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
      clearTimeout(blurTimer);
      clearTimeout(keyboardTimer);
      focusedField.value = null;
      focusSpace.value = 0;
      visibleArea.value = null;
      cancelReveal();
    });
  }, { immediate: true, flush: 'post' });

  watch(needsFocusSpace, updateViewport);

  return {
    viewportStyle,
    hasFocusSpace,
    focusField,
    blurField,
    cancelReveal,
    checkFieldVisible,
    onFocusIn,
    onFocusOut,
  };
}
